'use client';

import { cn } from '@/lib/utils';
import type { GeofenceOverlay } from '../DroneMap';
import { Trash2, Check, X } from 'lucide-react';

const ZONE_TYPE_BADGE: Record<string, { bg: string; label: string }> = {
  operational: { bg: 'bg-status-green/15 text-status-green-fg', label: 'OPS' },
  restricted: { bg: 'bg-status-red/15 text-status-red-fg', label: 'RSTR' },
  warning: { bg: 'bg-status-amber/15 text-status-amber-fg', label: 'WARN' },
  jamming: { bg: 'bg-status-red/15 text-status-red-fg border border-dashed border-status-red/40', label: 'JAM' },
};

interface ZonesListProps {
  geofences: GeofenceOverlay[];
  hasDrawMode: boolean;
  deleteConfirmId: string | null;
  onZoomToGeofence: (geometry: GeoJSON.Geometry) => void;
  onRequestDelete: (id: string | null) => void;
  onConfirmDelete: (id: string) => void;
}

export function ZonesList({
  geofences,
  hasDrawMode,
  deleteConfirmId,
  onZoomToGeofence,
  onRequestDelete,
  onConfirmDelete,
}: ZonesListProps) {
  return (
    <div className="flex-1 overflow-y-auto min-h-0">
      {geofences.length === 0 && !hasDrawMode ? (
        <div className="flex items-center justify-center py-8 text-muted-foreground text-xs">
          NO ZONES DEFINED
        </div>
      ) : (
        geofences.map((gf) => {
          const badge = ZONE_TYPE_BADGE[gf.type] || ZONE_TYPE_BADGE.operational;
          return (
            <div
              key={gf.id}
              className="flex items-center gap-2 px-3 py-2 border-b border-border row-hover transition-colors group"
            >
              <button
                type="button"
                onClick={() => onZoomToGeofence(gf.geometry)}
                className="flex-1 flex items-center gap-2 min-w-0 text-left"
                aria-label={`Zoom to zone ${gf.name}`}
              >
                <div
                  className="size-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: gf.color }}
                />
                <span className="text-xs text-foreground truncate uppercase">
                  {gf.name}
                </span>
                <span
                  className={cn(
                    'shrink-0 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider',
                    badge.bg,
                  )}
                >
                  {badge.label}
                </span>
              </button>
              {deleteConfirmId === gf.id ? (
                <div className="flex gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => onConfirmDelete(gf.id)}
                    className="p-1 text-status-red-fg hover:text-status-red"
                    aria-label="Confirm delete zone"
                    title="Confirm delete"
                  >
                    <Check className="size-3.5" strokeWidth={1.5} />
                  </button>
                  <button
                    type="button"
                    onClick={() => onRequestDelete(null)}
                    className="p-1 text-muted-foreground hover:text-foreground"
                    aria-label="Cancel delete zone"
                    title="Cancel"
                  >
                    <X className="size-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => onRequestDelete(gf.id)}
                  className="p-1 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                  aria-label={`Delete zone ${gf.name}`}
                  title="Delete zone"
                >
                  <Trash2 className="size-3.5" strokeWidth={1.5} />
                </button>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
